import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthConfig } from '@/shared/hooks/useAuthConfig'
import {
  prewarmAliyunCaptcha,
  runAliyunCaptcha,
  type CaptchaScene,
  type RunAliyunCaptchaOptions,
} from '@/shared/lib/aliyun-captcha'

function captchaOpts(
  config: NonNullable<ReturnType<typeof useAuthConfig>['config']>,
  scene: CaptchaScene,
  language: string,
): RunAliyunCaptchaOptions | null {
  const sceneId = config.captcha_scenes?.[scene]
  if (!config.captcha_prefix || !sceneId) return null
  return {
    prefix: config.captcha_prefix,
    region: config.captcha_region || 'cn',
    sceneId,
    language: language.startsWith('zh') ? 'cn' : 'en',
  }
}

/** 业务请求前触发阿里云验证码;未启用时直接执行 action */
export function useCaptchaGate() {
  const { config } = useAuthConfig()
  const { i18n } = useTranslation()

  const captchaEnabled = config?.captcha_enabled ?? false

  useEffect(() => {
    if (!captchaEnabled || !config) return
    // 仅预加载发码场景,避免多场景并行 init 互相 teardown DOM
    const opts = captchaOpts(config, 'sms_send', i18n.language)
    if (opts) prewarmAliyunCaptcha(opts)
  }, [captchaEnabled, config, i18n.language])

  const runWithCaptcha = useCallback(
    async (scene: CaptchaScene, action: (captchaVerifyParam?: string) => Promise<void>) => {
      if (!captchaEnabled || !config?.captcha_prefix) {
        await action(undefined)
        return
      }
      const opts = captchaOpts(config, scene, i18n.language)
      if (!opts) {
        await action(undefined)
        return
      }
      const param = await runAliyunCaptcha(opts)
      await action(param)
    },
    [captchaEnabled, config, i18n.language],
  )

  return { runWithCaptcha, captchaEnabled }
}
